import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useLingui } from '@lingui/react/macro';
import { useOnboardingFormStore } from '@/lib/onboarding-form-store';
import { useAppForm } from '@/lib/use-app-form';
import type { Locale } from '@worldcoo/monolith/core';
import { Card, CardContent, CardHeader, CardTitle } from '@worldcoo/ui/components/card';
import { step4Schema } from '@/lib/onboarding-schemas';

export const Route = createFileRoute('/ngos/onboarding/privacy')({
	component: RouteComponent,
});

function RouteComponent() {
	const { t, i18n } = useLingui();
	const countryFieldTexts = {
		search: t`Search...`,
		popular: t`Popular`,
		allCountries: t`All countries`,
		noCountryFound: t`No country found.`,
	};
	const regionFieldTexts = { search: t`Search...`, noResults: t`No results found.` };
	const provinceFieldTexts = { search: t`Search...`, noResults: t`No results found.` };
	const navigate = useNavigate();
	const { formData, setFormData, setCurrentStep } = useOnboardingFormStore();

	const form = useAppForm({
		defaultValues: {
			privacyEmail: formData.privacyEmail,
			privacyCountry: formData.privacyCountry || formData.country,
			privacyCity: formData.privacyCity,
			privacyRegion: formData.privacyRegion || formData.region,
			privacyProvince: formData.privacyProvince || formData.province,
			privacyPostalCode: formData.privacyPostalCode,
			privacyAddress: formData.privacyAddress,
			privacyPhone: formData.privacyPhone,
		},
		validators: {
			onSubmit: step4Schema,
		},
		onSubmit: async ({ value }) => {
			setFormData(value);
			setCurrentStep(5);
			navigate({ to: '/ngos/onboarding/terms', search: (prev) => prev });
		},
	});

	const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		const action = (e.nativeEvent as SubmitEvent).submitter?.getAttribute('value');

		if (action === 'back') {
			setFormData(form.state.values);
			setCurrentStep(3);
			navigate({ to: '/ngos/onboarding/accreditation', search: (prev) => prev });
			return;
		}

		form.handleSubmit();
	};

	return (
		<form id="onboarding-step-form" onSubmit={handleSubmit} className="flex flex-col gap-6">
			<Card className="shadow-warm">
				<CardHeader>
					<CardTitle className="text-xl">{t`Data protection officer`}</CardTitle>
				</CardHeader>
				<CardContent className="flex flex-col gap-6">
					<form.AppField
						name="privacyEmail"
						children={(field) => <field.TextField label={t`Data protection email`} placeholder="privacy@example.org" />}
					/>
					<form.AppField
						name="privacyPhone"
						children={(field) => <field.TextField label={t`Phone`} placeholder="+34..." />}
					/>
					<form.AppField name="privacyAddress" children={(field) => <field.TextField label={t`Address`} />} />
					<div className="flex gap-4">
						<form.AppField
							name="privacyCity"
							children={(field) => <field.TextField label={t`City`} className="w-full" />}
						/>
						<form.AppField
							name="privacyPostalCode"
							children={(field) => <field.TextField label={t`Postal Code`} className="w-full" />}
						/>
					</div>
					<form.AppField
						name="privacyCountry"
						listeners={{
							onChange: () => {
								form.setFieldValue('privacyRegion', '', { dontValidate: true, dontUpdateMeta: true });
								form.setFieldValue('privacyProvince', '', { dontValidate: true, dontUpdateMeta: true });
							},
						}}
						children={(field) => (
							<field.CountryField label={t`Country`} locale={i18n.locale as Locale} texts={countryFieldTexts} />
						)}
					/>
					<form.Subscribe
						selector={(state) => ({
							privacyCountry: state.values.privacyCountry,
							privacyRegion: state.values.privacyRegion,
						})}
					>
						{({ privacyCountry, privacyRegion }) => (
							<>
								<form.AppField
									name="privacyRegion"
									validators={{ onChangeListenTo: ['privacyCountry'] }}
									listeners={{
										onChange: () =>
											form.setFieldValue('privacyProvince', '', {
												dontValidate: true,
												dontUpdateMeta: true,
											}),
									}}
									children={(field) => (
										<field.RegionField label={t`Region`} country={privacyCountry} texts={regionFieldTexts} />
									)}
								/>
								<form.AppField
									name="privacyProvince"
									validators={{ onChangeListenTo: ['privacyCountry', 'privacyRegion'] }}
									children={(field) => (
										<field.ProvinceField
											label={t`Province`}
											region={privacyRegion}
											country={privacyCountry}
											texts={provinceFieldTexts}
										/>
									)}
								/>
							</>
						)}
					</form.Subscribe>
				</CardContent>
			</Card>
		</form>
	);
}
